import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import ChatIcon from '@mui/icons-material/Chat';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarRating from '../components/StarRating';
import Lightbox from '../components/Lightbox';
import { workerAPI, reviewAPI } from '../api/endpoints';
import { platformWhatsappLink } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import './WorkerDetail.css';

const EVAL_CRITERIA = [
  ['punctuality', 'Puntualidad'],
  ['work_quality', 'Calidad del trabajo'],
  ['cleanliness', 'Limpieza'],
  ['safety', 'Seguridad'],
  ['customer_treatment', 'Trato al cliente'],
];

// Reúne las imágenes de un proyecto (fotos subidas o, si no hay, la portada).
const projectImages = (p) => {
  if (p.photos && p.photos.length) return p.photos.map((ph) => ph.image);
  return p.cover_image ? [p.cover_image] : [];
};

const ProjectCard = ({ project, onOpen }) => {
  const images = projectImages(project);
  const [idx, setIdx] = useState(0);
  const multiple = images.length > 1;
  const go = (e, dir) => {
    e.stopPropagation();
    setIdx((i) => (i + dir + images.length) % images.length);
  };
  return (
    <div className="project-item">
      <div className="project-thumb">
        {images.length > 0 ? (
          <>
            <img
              src={images[idx]}
              alt={project.title}
              className="project-thumb-img"
              onClick={() => onOpen(images, idx)}
            />
            {multiple && (
              <>
                <button type="button" className="thumb-nav prev" onClick={(e) => go(e, -1)} aria-label="Foto anterior">
                  <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
                </button>
                <button type="button" className="thumb-nav next" onClick={(e) => go(e, 1)} aria-label="Foto siguiente">
                  <ArrowForwardIosIcon sx={{ fontSize: 15 }} />
                </button>
                <span className="thumb-counter">{idx + 1}/{images.length}</span>
              </>
            )}
          </>
        ) : (
          <span>Sin foto</span>
        )}
      </div>
      <div className="project-info">
        <h4>{project.title}</h4>
        {project.service_name && <span className="project-tag">{project.service_name}</span>}
        <p>{project.description}</p>
        <small>
          {project.location} {project.work_date && `· ${project.work_date}`}
        </small>
      </div>
    </div>
  );
};

const WorkerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('projects');
  const [lightbox, setLightbox] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 0, comment: '', service_hired: '' });
  const [reviewState, setReviewState] = useState({ sending: false, error: '', done: false });

  const loadWorker = () => {
    setLoading(true);
    workerAPI
      .detail(id)
      .then((r) => setWorker(r.data))
      .catch(() => setWorker(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadWorker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canReview = Boolean(token) && user?.role === 'client';

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.rating) {
      setReviewState({ sending: false, error: 'Selecciona una calificación.', done: false });
      return;
    }
    const clientName =
      `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'Cliente';
    setReviewState({ sending: true, error: '', done: false });
    try {
      await reviewAPI.create({
        worker: worker.id,
        client_name: clientName,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
        service_hired: reviewForm.service_hired,
      });
      setReviewForm({ rating: 0, comment: '', service_hired: '' });
      setReviewState({ sending: false, error: '', done: true });
      loadWorker();
    } catch (err) {
      setReviewState({
        sending: false,
        error: 'No se pudo enviar la reseña. Inténtalo de nuevo.',
        done: false,
      });
    }
  };

  if (loading) return <div className="detail-loading">Cargando perfil...</div>;
  if (!worker) return <div className="detail-loading">No se encontró el profesional.</div>;

  const evaluation = worker.supervisor_evaluation;
  const reviews = worker.client_reviews || [];
  const projects = worker.projects || [];
  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(
    worker.name || 'CM'
  )}&background=0b2545&color=fff&size=200`;
  const avatar = worker.profile_image || fallbackAvatar;

  return (
    <div className="detail-page">
      {/* Encabezado */}
      <div className="detail-hero">
        <div className="container detail-hero-inner">
          <div className="detail-avatar">
            <img
              src={avatar}
              alt={worker.name}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = fallbackAvatar;
              }}
            />
          </div>
          <div className="detail-headinfo">
            <div className="detail-name-row">
              <h1>{worker.name}</h1>
              {worker.is_verified && (
                <span className="verified-badge">
                  <VerifiedIcon sx={{ fontSize: 16 }} /> Verificado por Cuadrilla Maestra
                </span>
              )}
            </div>
            <p className="detail-headline">
              {worker.headline || worker.worker_type_display}
              {worker.category_name && ` · ${worker.category_name}`}
            </p>
            <div className="detail-meta">
              <StarRating value={worker.average_rating} showValue count={worker.reviews_count} />
              <span><LocationOnIcon sx={{ fontSize: 17 }} /> {worker.work_zone || 'Zona no indicada'}</span>
              <span><WorkHistoryIcon sx={{ fontSize: 17 }} /> {worker.years_experience} años de experiencia</span>
            </div>
            <div className="detail-actions">
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/contacto/${worker.id}`)}
              >
                <ChatIcon sx={{ fontSize: 18 }} /> Solicitar cotización
              </button>
              <a
                className="btn btn-whatsapp"
                href={platformWhatsappLink(worker)}
                target="_blank"
                rel="noreferrer"
              >
                <WhatsAppIcon sx={{ fontSize: 18 }} /> WhatsApp
              </a>
            </div>
          </div>

          <div className="detail-stats">
            <div><strong>{worker.projects_count}</strong><span>Proyectos</span></div>
            <div><strong>{worker.average_rating}</strong><span>Calificación</span></div>
            <div><strong>{worker.reviews_count}</strong><span>Reseñas</span></div>
          </div>
        </div>
      </div>

      <div className="container detail-body">
        <div className="detail-main">
          {/* Descripción */}
          {worker.bio && (
            <section className="detail-card">
              <h2>Descripción profesional</h2>
              <p className="detail-bio">{worker.bio}</p>
            </section>
          )}

          {/* Servicios */}
          {worker.services?.length > 0 && (
            <section className="detail-card">
              <h2>Servicios que ofrece</h2>
              <div className="detail-services">
                {worker.services.map((s) => (
                  <span key={s.id} className="detail-service">
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#1e9e6a' }} /> {s.name}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Tabs proyectos / reseñas */}
          <section className="detail-card">
            <div className="detail-tabs">
              <button
                className={tab === 'projects' ? 'active' : ''}
                onClick={() => setTab('projects')}
              >
                Proyectos ({projects.length})
              </button>
              <button
                className={tab === 'reviews' ? 'active' : ''}
                onClick={() => setTab('reviews')}
              >
                Reseñas y evaluación ({reviews.length + (evaluation ? 1 : 0)})
              </button>
            </div>

            {tab === 'projects' && (
              <div className="detail-projects">
                {projects.length === 0 && <p className="detail-muted">Aún no ha subido proyectos.</p>}
                {projects.map((p) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    onOpen={(images, index) => setLightbox({ images, index })}
                  />
                ))}
              </div>
            )}

            {tab === 'reviews' && (
              <div className="detail-reviews">
                {/* Formulario para que el cliente deje su reseña */}
                {canReview ? (
                  <form className="review-form" onSubmit={submitReview}>
                    <strong>Deja tu reseña</strong>
                    <div className="review-form-stars">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          className="review-star-btn"
                          onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                          aria-label={`${n} estrellas`}
                        >
                          {n <= reviewForm.rating ? (
                            <StarIcon sx={{ fontSize: 28, color: '#ff6a00' }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 28, color: '#ff6a00' }} />
                          )}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder="Servicio contratado (opcional). Ej: Pintura de fachada"
                      value={reviewForm.service_hired}
                      onChange={(e) =>
                        setReviewForm((f) => ({ ...f, service_hired: e.target.value }))
                      }
                    />
                    <textarea
                      rows={3}
                      placeholder="Cuéntanos tu experiencia con este maestro..."
                      value={reviewForm.comment}
                      onChange={(e) =>
                        setReviewForm((f) => ({ ...f, comment: e.target.value }))
                      }
                    />
                    {reviewState.error && (
                      <p className="review-form-error">{reviewState.error}</p>
                    )}
                    {reviewState.done && (
                      <p className="review-form-ok">¡Gracias! Tu reseña se publicó.</p>
                    )}
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={reviewState.sending}
                    >
                      {reviewState.sending ? 'Enviando...' : 'Publicar reseña'}
                    </button>
                  </form>
                ) : (
                  !token && (
                    <p className="review-login-prompt">
                      <Link to="/login">Inicia sesión</Link> como cliente para dejar una
                      reseña y calificar a este maestro.
                    </p>
                  )
                )}

                {/* Evaluación del supervisor (diferenciada) */}
                {evaluation && (
                  <div className="supervisor-eval">
                    <div className="supervisor-eval-head">
                      <ShieldIcon sx={{ fontSize: 22, color: '#0e2433' }} />
                      <div>
                        <strong>Evaluación verificada por Cuadrilla Maestra</strong>
                        <span>
                          Supervisor: {evaluation.supervisor_name} · {evaluation.evaluated_at}
                        </span>
                      </div>
                      <div className="supervisor-score">{evaluation.overall_score}</div>
                    </div>
                    <div className="supervisor-criteria">
                      {EVAL_CRITERIA.map(([key, label]) => (
                        <div key={key} className="criteria-row">
                          <span>{label}</span>
                          <StarRating value={evaluation[key]} size={15} />
                        </div>
                      ))}
                    </div>
                    {evaluation.comment && (
                      <p className="supervisor-comment">“{evaluation.comment}”</p>
                    )}
                  </div>
                )}

                {/* Reseñas de clientes */}
                {reviews.length === 0 && !evaluation && (
                  <p className="detail-muted">Aún no tiene reseñas.</p>
                )}
                {reviews.map((rv) => (
                  <div key={rv.id} className="review-item">
                    <div className="review-head">
                      <div className="review-avatar">
                        {rv.client_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <strong>{rv.client_name}</strong>
                        <span>{rv.service_hired} · {new Date(rv.created_at).toLocaleDateString()}</span>
                      </div>
                      <StarRating value={rv.rating} size={16} />
                    </div>
                    {rv.comment && <p>{rv.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Lateral */}
        <aside className="detail-side">
          <div className="detail-card detail-contact-card">
            <h3>¿Te interesa este maestro?</h3>
            <p>Solicita una cotización gratuita y sin compromiso.</p>
            <button
              className="btn btn-primary detail-side-btn"
              onClick={() => navigate(`/contacto/${worker.id}`)}
            >
              <ChatIcon sx={{ fontSize: 18 }} /> Solicitar cotización
            </button>
            <a
              className="btn btn-whatsapp detail-side-btn"
              href={platformWhatsappLink(worker)}
              target="_blank"
              rel="noreferrer"
            >
              <WhatsAppIcon sx={{ fontSize: 18 }} /> Escribir por WhatsApp
            </a>
            <Link to="/buscar" className="detail-back">← Volver a la búsqueda</Link>
          </div>
        </aside>
      </div>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

export default WorkerDetail;
